import { useState } from 'react'
import { MRT_ColumnDef } from 'material-react-table'
import Add from '@mui/icons-material/Add'
import Delete from '@mui/icons-material/Delete'
import Edit from '@mui/icons-material/Edit'
import { Button, Container, IconButton, Stack } from '@mui/material'
import { useStytchIsAuthorized } from '@stytch/react/b2b'
import useOverlay from '@/hooks/useOverlay'
import {
  useDeleteOrgType,
  useGetOrgTypes,
} from '@/service-library/hooks/org-types'
import { OrgType } from '@/types/organizations'
import { showErrorSnackbar } from '@/utils/snackbars'
import useZerapixTable from '@/components/zerapix-table/useZerapixTable'
import ZerapixTable from '@/components/zerapix-table/ZerapixTable'
import AddEditOrgTypeDialog from './AddEditOrgTypeDialog'

const columns: MRT_ColumnDef<OrgType>[] = [
  {
    header: 'Name',
    accessorKey: 'name',
  },
]

export default function OrganizationTypesTable() {
  const [type, setType] = useState<OrgType>()
  const addEditOrgTypeOverlay = useOverlay()
  const { isAuthorized: canEditOrg } = useStytchIsAuthorized('pd.org', 'edit')

  const { orgTypes, isPending, queryKey } = useGetOrgTypes({
    filters: {
      limit: '1000',
    },
  })

  const { deleteOrgType } = useDeleteOrgType({
    listQueryKey: queryKey,
    onError: () => {
      showErrorSnackbar('Unable to delete type. Please try again later.')
    },
  })

  const table = useZerapixTable<OrgType>({
    title: 'Organization Types',
    columns,
    data: orgTypes,
    displayColumnDefOptions: {
      'mrt-row-actions': {
        size: 85,
      },
    },
    enableRowActions: canEditOrg,
    enablePagination: false,
    initialState: { density: 'comfortable' },
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => (
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton
          color="primary"
          size="small"
          onClick={() => {
            setType(row.original)
            addEditOrgTypeOverlay.open()
          }}
        >
          <Edit fontSize="small" />
        </IconButton>
        <IconButton
          color="error"
          size="small"
          onClick={() => {
            deleteOrgType(row.original.id)
          }}
        >
          <Delete fontSize="small" />
        </IconButton>
      </Stack>
    ),
    renderTableActions: canEditOrg
      ? () => {
          return (
            <Button
              onClick={() => {
                setType(undefined)
                addEditOrgTypeOverlay.open()
              }}
              startIcon={<Add />}
              variant="text"
            >
              New Type
            </Button>
          )
        }
      : undefined,
    state: { isLoading: isPending },
  })

  return (
    <Container maxWidth="sm">
      <ZerapixTable<OrgType> table={table} />

      <AddEditOrgTypeDialog
        overlay={addEditOrgTypeOverlay}
        organizationType={type}
        listQueryKey={queryKey}
      />
    </Container>
  )
}
